import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Trash, Calculator, ChartLineUp, Stack, Package,
  FilePdf, FileXls, FloppyDisk, FolderOpen, Calendar, Buildings,
  ArrowsHorizontal, MagnifyingGlass, Scales, CurrencyInr, Eye,
  ShoppingCart, Cube, Receipt, ChartLine, User,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "../components/ui/command";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { api, API, fmtINR, TOKEN_KEY } from "../lib/kostra";
import { useAuth } from "../lib/auth";

const PRODUCTION_FOR = ["Restaurant", "Cloud Kitchen", "Bulk Order", "Catering", "Event", "Other"];

const todayISO = () => new Date().toISOString().slice(0, 10);

const computeLocal = (rows) => {
  // Mirror backend math so UI updates instantly.
  const items = rows.map((r) => {
    const nw = +r.net_weight_per_portion_g || 0;
    const cpp = +r.cost_per_portion || 0;
    const waste = Math.max(0, +r.waste_pct || 0);
    const wm = 1 + waste / 100;
    let tp = +r.target_portions || 0;
    let tw = +r.target_total_weight_kg || 0;
    if (r.mode === "weight") {
      tp = (nw > 0 && tw > 0) ? (tw * 1000) / nw : 0;
    } else {
      tw = nw > 0 ? (tp * nw) / 1000 : 0;
    }
    const effPostWaste = tp * wm;
    const produced_portions = effPostWaste > 0 ? Math.ceil(effPostWaste) : 0;
    const produced_weight_kg = nw > 0 ? (produced_portions * nw) / 1000 : 0;
    const total_cost = +(produced_portions * cpp).toFixed(2);
    return {
      ...r,
      target_portions: +tp.toFixed(2),
      target_total_weight_kg: +tw.toFixed(3),
      effective_portions: produced_portions,
      total_weight_kg: +produced_weight_kg.toFixed(3),
      total_cost,
    };
  });
  const total_cost = items.reduce((s, i) => s + i.total_cost, 0);
  const total_weight_kg = items.reduce((s, i) => s + i.total_weight_kg, 0);
  const total_portions = items.reduce((s, i) => s + i.effective_portions, 0);
  const avg = total_weight_kg > 0 ? total_cost / total_weight_kg : 0;
  return {
    items,
    totals: {
      total_cost: +total_cost.toFixed(2),
      total_weight_kg: +total_weight_kg.toFixed(3),
      total_portions,
      avg_cost_per_kg: +avg.toFixed(2),
      item_count: items.length,
    },
  };
};

export default function ProductionPlanPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { guard, isAdmin } = useAuth();
  const [savedRecipes, setSavedRecipes] = useState([]);
  const [savedPlans, setSavedPlans] = useState([]);
  const [loadingRecipes, setLoadingRecipes] = useState(true);

  const [planName, setPlanName] = useState(`Plan ${todayISO()}`);
  const [productionDate, setProductionDate] = useState(todayISO());
  const [productionFor, setProductionFor] = useState("Restaurant");
  const [brand, setBrand] = useState("");
  const [outlet, setOutlet] = useState("");
  const [preparedBy, setPreparedBy] = useState("");
  const [notes, setNotes] = useState("");
  const [rows, setRows] = useState([]);
  const [ingredientOverrides, setIngredientOverrides] = useState({}); // {key: {available_stock_kg, actual_cost}}
  const [requirementSheet, setRequirementSheet] = useState({ items: [], totals: null });
  const [activePlanId, setActivePlanId] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  // Load saved recipes
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/recipes");
        setSavedRecipes(data || []);
      } catch { toast.error("Failed to load recipes"); }
      finally { setLoadingRecipes(false); }
    })();
  }, []);

  // Load saved plans for admin
  const refreshPlans = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/production/v2/plans");
      setSavedPlans(data || []);
    } catch (e) {
      // best-effort load
    }
  };
  useEffect(() => { refreshPlans(); }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load a plan via ?plan=<id>
  useEffect(() => {
    const pid = searchParams.get("plan");
    if (!pid || !savedRecipes.length) return;
    (async () => {
      try {
        const { data } = await api.get(`/production/v2/plans/${pid}`);
        hydrateFromPlan(data);
      } catch (e) {
        // ignore
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, savedRecipes]);

  const hydrateFromPlan = (plan) => {
    setActivePlanId(plan.id);
    setPlanName(plan.name);
    setProductionDate(plan.production_date || todayISO());
    setProductionFor(plan.production_for || "Restaurant");
    setBrand(plan.brand || "");
    setOutlet(plan.outlet || "");
    setPreparedBy(plan.prepared_by || "");
    setNotes(plan.notes || "");
    // Hydrate ingredient overrides from saved requirements
    const ov = {};
    (plan.ingredient_requirements || []).forEach((ing) => {
      if (!ing.key) return;
      ov[ing.key] = {
        available_stock_kg: ing.available_stock_kg || 0,
        actual_cost: ing.actual_cost || 0,
      };
    });
    setIngredientOverrides(ov);
    setRequirementSheet({
      items: plan.ingredient_requirements || [],
      totals: plan.requirement_totals || null,
    });
    setRows((plan.items || []).map((it) => ({
      id: it.id || crypto.randomUUID(),
      recipe_id: it.recipe_id,
      recipe_name: it.recipe_name,
      category: it.category,
      net_weight_per_portion_g: it.net_weight_per_portion_g,
      base_num_portions: it.base_num_portions,
      cost_per_portion: it.cost_per_portion,
      cost_per_kg: it.cost_per_kg,
      mode: it.mode || "portions",
      target_portions: it.target_portions || 0,
      target_total_weight_kg: it.target_total_weight_kg || 0,
      waste_pct: it.waste_pct || 0,
    })));
    toast.success(`Loaded "${plan.name}"`);
  };

  const recipePickerItems = useMemo(() => {
    const q = (search || "").toLowerCase().trim();
    const list = savedRecipes.map((r) => {
      const s = r.summary || {};
      return {
        recipe_id: r.id,
        recipe_name: r.dish_name || "Untitled",
        category: r.category || "—",
        net_weight_per_portion_g: +s.net_weight_per_portion_g || 0,
        base_num_portions: +r.num_portions || 1,
        cost_per_portion: +s.cost_per_portion || 0,
        cost_per_kg: +s.cost_per_kg || 0,
        photo_path: r.photo_path,
      };
    });
    if (!q) return list.slice(0, 200);
    return list.filter((it) =>
      it.recipe_name.toLowerCase().includes(q) ||
      it.category.toLowerCase().includes(q)
    ).slice(0, 200);
  }, [savedRecipes, search]);

  const addRecipe = (item) => {
    if (rows.some((r) => r.recipe_id === item.recipe_id)) {
      toast.error(`${item.recipe_name} already added`);
      return;
    }
    if (!item.net_weight_per_portion_g) {
      toast.warning(`"${item.recipe_name}" has no Net Weight set — open it in Calculator first.`);
    }
    setRows([
      ...rows,
      {
        id: crypto.randomUUID(),
        recipe_id: item.recipe_id,
        recipe_name: item.recipe_name,
        category: item.category,
        net_weight_per_portion_g: item.net_weight_per_portion_g,
        base_num_portions: item.base_num_portions,
        cost_per_portion: item.cost_per_portion,
        cost_per_kg: item.cost_per_kg,
        mode: "portions",
        target_portions: item.base_num_portions || 10,
        target_total_weight_kg: 0,
        waste_pct: 0,
      },
    ]);
    setPickerOpen(false);
    setSearch("");
  };

  const updateRow = (id, patch) => {
    setRows(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };
  const removeRow = (id) => setRows(rows.filter((r) => r.id !== id));

  const live = useMemo(() => computeLocal(rows), [rows]);

  // Live ingredient explosion via backend (debounced via rows length signature)
  useEffect(() => {
    if (rows.length === 0) {
      setRequirementSheet({ items: [], totals: null });
      return;
    }
    if (!isAdmin) return;
    const tid = setTimeout(async () => {
      try {
        const { data } = await api.post("/production/v2/compute", {
          items: rows.map((r) => ({
            id: r.id, recipe_id: r.recipe_id, mode: r.mode,
            target_portions: r.target_portions || 0,
            target_total_weight_kg: r.target_total_weight_kg || 0,
            waste_pct: r.waste_pct || 0,
          })),
          ingredient_overrides: Object.entries(ingredientOverrides).map(([key, v]) => ({
            key,
            available_stock_kg: v.available_stock_kg || 0,
            actual_cost: v.actual_cost || 0,
          })),
        });
        setRequirementSheet({
          items: data.ingredient_requirements || [],
          totals: data.requirement_totals || null,
        });
      } catch (e) {
        // Best-effort; UI will just show stale data
      }
    }, 300);
    return () => clearTimeout(tid);
  }, [rows, ingredientOverrides, isAdmin]);

  // ---- Persist ----
  const buildPayload = () => ({
    name: planName.trim() || `Plan ${productionDate}`,
    production_date: productionDate,
    production_for: productionFor,
    brand: brand.trim(),
    outlet: outlet.trim(),
    prepared_by: preparedBy.trim(),
    notes,
    items: rows.map((r) => ({
      id: r.id,
      recipe_id: r.recipe_id,
      mode: r.mode,
      target_portions: r.target_portions || 0,
      target_total_weight_kg: r.target_total_weight_kg || 0,
      waste_pct: r.waste_pct || 0,
    })),
    ingredient_overrides: Object.entries(ingredientOverrides).map(([key, v]) => ({
      key,
      available_stock_kg: v.available_stock_kg || 0,
      actual_cost: v.actual_cost || 0,
    })),
  });

  const savePlan = guard(async () => {
    if (!rows.length) { toast.error("Add at least one recipe"); return; }
    setSaving(true);
    try {
      const payload = buildPayload();
      let saved;
      if (activePlanId) {
        const { data } = await api.put(`/production/v2/plans/${activePlanId}`, payload);
        saved = data; toast.success("Plan updated");
      } else {
        const { data } = await api.post("/production/v2/plans", payload);
        saved = data; toast.success("Plan saved");
      }
      setActivePlanId(saved.id);
      refreshPlans();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }, "save the plan");

  const downloadFile = async (kind) => {
    if (!activePlanId) { toast.error("Save the plan first to download"); return; }
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const url = `${API}/production/v2/plans/${activePlanId}/export/${kind}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `KOSTRA_Production_Plan_${planName.replace(/[^a-z0-9]+/gi, "_")}.${kind === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(`${kind.toUpperCase()} downloaded`);
    } catch (e) { toast.error("Download failed"); }
  };

  const exportExcel = guard(() => downloadFile("excel"), "download Excel");
  const exportPdf = guard(() => downloadFile("pdf"), "download PDF");

  const previewPlan = guard(() => {
    if (!activePlanId) { toast.error("Save the plan first to preview"); return; }
    navigate(`/production/preview/${activePlanId}`);
  }, "preview the plan");

  const newPlan = () => {
    setActivePlanId(null);
    setPlanName(`Plan ${todayISO()}`);
    setProductionDate(todayISO());
    setProductionFor("Restaurant");
    setBrand("");
    setOutlet("");
    setPreparedBy("");
    setNotes("");
    setRows([]);
    setIngredientOverrides({});
    setRequirementSheet({ items: [], totals: null });
    toast.message("New plan started");
  };

  return (
    <div className="app-shell">
      <Header />

      {/* Hero */}
      <div className="bg-gradient-to-b from-[#0A3B24] to-[#0E5232] text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-8 pb-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="text-[#D4AF37] text-xs tracking-[0.25em] uppercase font-medium inline-flex items-center gap-2">
                <Package size={14} weight="fill" /> Production Plan
              </div>
              <h1 className="font-display text-4xl md:text-5xl font-medium mt-2 leading-tight">
                Plan the kitchen, <br className="hidden md:block" />
                <span className="text-[#D4AF37]">cost the day.</span>
              </h1>
              <p className="text-white/75 text-sm md:text-base mt-3 max-w-2xl">
                Build a structured production plan with target portions or target weight, factor in waste, and
                instantly see the total cost &amp; weight. Save it, export it, replay it.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Link to="/production/history">
                <Button
                  variant="outline"
                  className="bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white font-medium h-11"
                  data-testid="nav-prod-history"
                >
                  <ChartLineUp size={16} weight="bold" className="mr-2" /> History &amp; Analytics
                </Button>
              </Link>
              {(rows.length > 0 || activePlanId) && (
                <Button
                  onClick={newPlan}
                  variant="outline"
                  className="bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white font-medium h-11"
                  data-testid="plan-new-btn"
                >
                  <Plus size={16} weight="bold" className="mr-2" /> New
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-10 space-y-8">
        {activePlanId && (
          <div className="kostra-card p-3 px-4 inline-flex items-center gap-2"
               data-testid="plan-active-pill">
            <FolderOpen size={14} weight="duotone" className="text-[#D4AF37]" />
            <span className="text-sm">
              Editing saved plan — <strong className="text-[#0A3B24]">{planName}</strong>
            </span>
          </div>
        )}

        {/* Plan Header card */}
        <section className="kostra-card p-5 md:p-6" data-testid="plan-header-card">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            <div className="md:col-span-4">
              <Label className="kostra-data-label">Plan Name</Label>
              <Input
                value={planName} onChange={(e) => setPlanName(e.target.value)}
                placeholder="e.g. Saturday Buffet"
                className="mt-1 h-11"
                data-testid="plan-name-input"
              />
            </div>
            <div className="md:col-span-3">
              <Label className="kostra-data-label inline-flex items-center gap-1.5">
                <Calendar size={12} weight="duotone" /> Production Date
              </Label>
              <Input
                type="date" value={productionDate}
                onChange={(e) => setProductionDate(e.target.value)}
                className="mt-1 h-11 font-mono-num"
                data-testid="plan-date-input"
              />
            </div>
            <div className="md:col-span-3">
              <Label className="kostra-data-label inline-flex items-center gap-1.5">
                <Buildings size={12} weight="duotone" /> Production For
              </Label>
              <Select value={productionFor} onValueChange={setProductionFor}>
                <SelectTrigger className="mt-1 h-11" data-testid="plan-for-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRODUCTION_FOR.map((o) => (
                    <SelectItem key={o} value={o} data-testid={`plan-for-${o.replace(/\s+/g, '-').toLowerCase()}`}>
                      {o}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 flex flex-wrap gap-2 items-end">
              <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    className="bg-[#0A3B24] hover:bg-[#0E5232] text-white font-semibold h-11 w-full"
                    data-testid="plan-add-recipe-btn"
                  >
                    <Plus size={16} weight="bold" className="mr-2" /> Add Recipe
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[440px]" align="end">
                  <Command shouldFilter={false}>
                    <CommandInput placeholder="Search recipes..." value={search} onValueChange={setSearch} />
                    <CommandList className="max-h-96">
                      <CommandEmpty>
                        {loadingRecipes ? "Loading..." : "No recipes found."}
                      </CommandEmpty>
                      <CommandGroup heading="My Recipes">
                        {recipePickerItems.map((it) => (
                          <CommandItem
                            key={it.recipe_id} value={it.recipe_id}
                            onSelect={() => addRecipe(it)}
                            data-testid={`plan-pick-${it.recipe_id}`}
                          >
                            <div className="flex items-center gap-3 w-full">
                              <div className="h-10 w-10 rounded-md bg-[#F9FAFB] border border-[#E5E7EB] overflow-hidden shrink-0">
                                {it.photo_path && (
                                  <img src={`${API}/files/${it.photo_path}`} alt="" className="w-full h-full object-cover" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-[#111827] truncate">{it.recipe_name}</div>
                                <div className="text-[11px] text-[#4B5563] mt-0.5 flex items-center gap-1.5 flex-wrap">
                                  <span>{it.category}</span>
                                  <span className="text-[#D4AF37]">·</span>
                                  <span className="font-mono-num">{fmtINR(it.cost_per_portion)}/port</span>
                                  {it.net_weight_per_portion_g > 0 && (
                                    <>
                                      <span className="text-[#D4AF37]">·</span>
                                      <span className="font-mono-num">{it.net_weight_per_portion_g} g</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-12 gap-4">
            <div className="md:col-span-3">
              <Label className="kostra-data-label">Brand (optional)</Label>
              <Input
                value={brand} onChange={(e) => setBrand(e.target.value)}
                placeholder="e.g. Kostra Kitchen"
                className="mt-1 h-10"
                data-testid="plan-brand-input"
              />
            </div>
            <div className="md:col-span-3">
              <Label className="kostra-data-label">Outlet (optional)</Label>
              <Input
                value={outlet} onChange={(e) => setOutlet(e.target.value)}
                placeholder="e.g. Andheri West"
                className="mt-1 h-10"
                data-testid="plan-outlet-input"
              />
            </div>
            <div className="md:col-span-3">
              <Label className="kostra-data-label">Prepared By (optional)</Label>
              <Input
                value={preparedBy} onChange={(e) => setPreparedBy(e.target.value)}
                placeholder="e.g. Chef Anupam"
                className="mt-1 h-10"
                data-testid="plan-prepared-by-input"
              />
            </div>
            <div className="md:col-span-3">
              <Label className="kostra-data-label">Notes (optional)</Label>
              <Textarea
                value={notes} onChange={(e) => setNotes(e.target.value)}
                rows={1}
                placeholder="Special instructions..."
                className="mt-1"
                data-testid="plan-notes-input"
              />
            </div>
          </div>
        </section>

        {/* Line items */}
        <section className="kostra-card p-5 md:p-6" data-testid="plan-rows-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-md bg-[#0A3B24]/10 text-[#0A3B24] flex items-center justify-center">
                <Stack size={20} weight="duotone" />
              </div>
              <div>
                <h2 className="kostra-section-title text-xl">Production Lines</h2>
                <p className="text-xs text-[#4B5563] mt-0.5">
                  Choose <strong>Portions</strong> or <strong>Weight</strong> mode per line — KOSTRA derives the other.
                </p>
              </div>
            </div>
            <span className="gold-pill text-[11px] font-semibold px-2.5 py-1 rounded-full">
              {rows.length} line{rows.length !== 1 ? "s" : ""}
            </span>
          </div>

          {rows.length === 0 ? (
            <div className="rounded-md border-2 border-dashed border-[#E5E7EB] bg-[#F9FAFB] py-10 text-center" data-testid="plan-empty">
              <Package size={36} weight="duotone" className="mx-auto text-[#0A3B24]" />
              <div className="text-sm text-[#111827] font-semibold mt-2">No lines yet</div>
              <div className="text-xs text-[#4B5563] mt-1">
                Click <strong>Add Recipe</strong> to start building your plan.
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <AnimatePresence initial={false}>
                {live.items.map((row, idx) => (
                  <motion.div
                    key={row.id}
                    layout
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.2 }}
                    className="rounded-xl border border-[#E5E7EB] bg-white hover:border-[#0A3B24]/40 hover:shadow-sm transition-all overflow-hidden"
                    data-testid={`plan-row-${idx}`}
                  >
                    {/* HEADER */}
                    <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-[#FDF9EE]/60 to-white border-b border-[#F1E9D0]">
                      <div className="h-9 w-9 rounded-md bg-[#0A3B24] text-white font-mono-num font-bold inline-flex items-center justify-center text-sm shrink-0">
                        {idx + 1}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-[#0A3B24] truncate text-base">{row.recipe_name}</div>
                        <div className="text-[11px] text-[#4B5563] mt-0.5 flex items-center gap-2 flex-wrap">
                          <span>{row.category || "—"}</span>
                          <span className="text-[#D4AF37]">·</span>
                          <span className="font-mono-num">Net Wt {row.net_weight_per_portion_g} g/port</span>
                          <span className="text-[#D4AF37]">·</span>
                          <span className="font-mono-num">Cost {fmtINR(row.cost_per_portion)}/port</span>
                          <span className="text-[#D4AF37]">·</span>
                          <span className="font-mono-num">{fmtINR(row.cost_per_kg)}/kg</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeRow(row.id)}
                        className="h-9 w-9 rounded-md text-[#4B5563] hover:bg-[#FBE8E8] hover:text-[#DC2626] transition inline-flex items-center justify-center shrink-0"
                        data-testid={`plan-row-remove-${idx}`}
                      >
                        <Trash size={16} weight="bold" />
                      </button>
                    </div>

                    {/* INPUTS */}
                    <div className="px-4 py-4 grid grid-cols-1 sm:grid-cols-12 gap-x-4 gap-y-3 items-end">
                      <div className="sm:col-span-3">
                        <Label className="kostra-data-label mb-1.5 inline-flex items-center gap-1.5">
                          <ArrowsHorizontal size={11} weight="bold" /> Quantity Mode
                        </Label>
                        <Select value={row.mode} onValueChange={(v) => updateRow(row.id, { mode: v })}>
                          <SelectTrigger className="h-10" data-testid={`plan-row-mode-${idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="portions">By Portions</SelectItem>
                            <SelectItem value="weight">By Total Weight (kg)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {row.mode === "portions" ? (
                        <div className="sm:col-span-3">
                          <Label className="kostra-data-label mb-1.5 inline-flex items-center gap-1.5">
                            <Stack size={11} weight="bold" /> Target Portions
                          </Label>
                          <Input
                            type="number" min="0" step="1"
                            value={row.target_portions || 0}
                            onChange={(e) => updateRow(row.id, { target_portions: parseFloat(e.target.value) || 0 })}
                            className="h-10 font-mono-num border-2 focus-visible:border-[#0A3B24]"
                            data-testid={`plan-row-portions-${idx}`}
                          />
                          <div className="text-[10px] text-[#4B5563] mt-1 font-mono-num">
                            = {row.target_total_weight_kg.toFixed(3)} kg total
                          </div>
                        </div>
                      ) : (
                        <div className="sm:col-span-3">
                          <Label className="kostra-data-label mb-1.5 inline-flex items-center gap-1.5">
                            <Scales size={11} weight="bold" /> Target Total Weight (kg)
                          </Label>
                          <Input
                            type="number" min="0" step="0.1"
                            value={row.target_total_weight_kg || 0}
                            onChange={(e) => updateRow(row.id, { target_total_weight_kg: parseFloat(e.target.value) || 0 })}
                            className="h-10 font-mono-num border-2 focus-visible:border-[#0A3B24]"
                            data-testid={`plan-row-weight-${idx}`}
                          />
                          <div className="text-[10px] text-[#4B5563] mt-1 font-mono-num">
                            ≈ {Math.ceil(row.target_portions || 0)} portions
                          </div>
                        </div>
                      )}
                      <div className="sm:col-span-2">
                        <Label className="kostra-data-label mb-1.5">Waste %</Label>
                        <div className="relative">
                          <Input
                            type="number" min="0" max="50" step="1"
                            value={row.waste_pct || 0}
                            onChange={(e) => updateRow(row.id, { waste_pct: parseFloat(e.target.value) || 0 })}
                            className="h-10 font-mono-num border-2 pr-8 focus-visible:border-[#0A3B24]"
                            placeholder="0"
                            data-testid={`plan-row-waste-${idx}`}
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4B5563] text-xs font-semibold pointer-events-none">%</span>
                        </div>
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="kostra-data-label mb-1.5">Produced Portions</Label>
                        <div className="h-10 px-3 rounded-md border-2 border-dashed border-[#D4AF37]/40 bg-[#FDF9EE] inline-flex items-center justify-center w-full">
                          <span className="font-mono-num text-base text-[#0A3B24] font-semibold">
                            {row.effective_portions}
                          </span>
                        </div>
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="kostra-data-label mb-1.5">Total Cost</Label>
                        <div className="h-10 px-3 rounded-md border-2 border-dashed border-[#0A3B24]/30 bg-[#0A3B24]/5 inline-flex items-center justify-center w-full">
                          <span className="font-mono-num text-sm text-[#0A3B24] font-bold">
                            {fmtINR(row.total_cost)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </section>

        {/* Live totals */}
        {rows.length > 0 && (
          <section className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="plan-totals">
            <StatCard label="Items" value={live.totals.item_count} icon={Stack} accent="#0A3B24" />
            <StatCard label="Total Portions" value={live.totals.total_portions} icon={Stack} accent="#0E5232" />
            <StatCard label="Total Weight" value={`${live.totals.total_weight_kg.toFixed(3)} kg`} icon={Scales} accent="#D4AF37" />
            <StatCard label="Total Cost" value={fmtINR(live.totals.total_cost)} icon={CurrencyInr} accent="#0A3B24" />
            <StatCard label="Avg Cost / Kg" value={fmtINR(live.totals.avg_cost_per_kg)} icon={CurrencyInr} accent="#16A34A" />
          </section>
        )}

        {/* Section C–F: Ingredient Requirement, Purchase Requisition, Actual Spend, Variance */}
        {rows.length > 0 && (requirementSheet.items.length > 0) && (
          <IngredientControlSection
            requirements={requirementSheet.items}
            totals={requirementSheet.totals}
            overrides={ingredientOverrides}
            onOverrideChange={(key, patch) => {
              setIngredientOverrides({
                ...ingredientOverrides,
                [key]: { ...(ingredientOverrides[key] || {}), ...patch },
              });
            }}
            onDownloadRequisition={guard(async () => {
              if (!activePlanId) { toast.error("Save the plan first to download requisition"); return; }
              try {
                const token = localStorage.getItem(TOKEN_KEY);
                const res = await fetch(
                  `${API}/production/v2/plans/${activePlanId}/export/requisition`,
                  { headers: { Authorization: `Bearer ${token}` } },
                );
                if (!res.ok) throw new Error();
                const blob = await res.blob();
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = `KOSTRA_Purchase_Requisition_${planName.replace(/[^a-z0-9]+/gi, "_")}.xlsx`;
                document.body.appendChild(a); a.click(); a.remove();
                toast.success("Requisition downloaded");
              } catch { toast.error("Download failed"); }
            }, "download requisition")}
            onEmailVendor={() => {
              // mailto fallback — works in all browsers, no integration needed
              const lines = requirementSheet.items
                .filter((x) => (x.purchase_qty_kg || 0) > 0)
                .map((x) => `• ${x.name}: ${x.purchase_qty_kg.toFixed(2)} kg (₹${x.purchase_value.toFixed(2)})`);
              if (!lines.length) { toast.error("Nothing to purchase — all items in stock"); return; }
              const subject = encodeURIComponent(`Purchase Requisition — ${planName}`);
              const body = encodeURIComponent(
                `Hi Team,\n\nPlease procure the following for ${productionDate} (${productionFor}):\n\n${lines.join("\n")}\n\nTotal: ₹${(requirementSheet.totals?.purchase_value || 0).toFixed(2)}\n\n— ${preparedBy || "KOSTRA"}`,
              );
              window.location.href = `mailto:?subject=${subject}&body=${body}`;
            }}
          />
        )}

        {/* Action bar */}
        <section className="kostra-card p-5 flex flex-wrap items-center justify-between gap-3" data-testid="plan-actions">
          <div className="text-sm text-[#4B5563]">
            {activePlanId ? "Editing saved plan — totals recompute on every change." : "New plan — save to enable downloads."}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={savePlan}
              disabled={isAdmin && (rows.length === 0 || saving)}
              className="bg-[#0A3B24] hover:bg-[#0E5232] text-white font-semibold h-11"
              data-testid="plan-save-btn"
            >
              <FloppyDisk size={16} weight="bold" className="mr-2" />
              {saving ? "Saving..." : (activePlanId ? "Update Plan" : "Save Plan")}
            </Button>
            <Button
              onClick={previewPlan}
              disabled={isAdmin && !activePlanId}
              variant="outline"
              className="bg-white border-[#0A3B24]/30 text-[#0A3B24] hover:bg-[#FDF9EE] font-semibold h-11"
              data-testid="plan-preview-btn"
            >
              <Eye size={16} weight="bold" className="mr-2" /> Preview
            </Button>
            <Button
              onClick={exportExcel}
              disabled={isAdmin && !activePlanId}
              className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold h-11"
              data-testid="plan-excel-btn"
            >
              <FileXls size={16} weight="bold" className="mr-2" /> Save as Excel
            </Button>
            <Button
              onClick={exportPdf}
              disabled={isAdmin && !activePlanId}
              variant="outline"
              className="bg-white border-[#0A3B24]/30 text-[#0A3B24] hover:bg-[#FDF9EE] font-semibold h-11"
              data-testid="plan-pdf-btn"
            >
              <FilePdf size={16} weight="bold" className="mr-2" /> PDF
            </Button>
          </div>
        </section>

        {/* Saved Plans Picker */}
        {isAdmin && savedPlans.length > 0 && (
          <section className="kostra-card p-5" data-testid="plan-saved-list">
            <div className="flex items-center justify-between mb-3">
              <h3 className="kostra-section-title text-lg inline-flex items-center gap-2">
                <FolderOpen size={18} weight="duotone" /> Saved Plans
              </h3>
              <Link to="/production/history" className="text-xs text-[#0A3B24] hover:underline font-semibold">
                Open History &amp; Analytics →
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {savedPlans.slice(0, 9).map((p) => (
                <button
                  key={p.id}
                  onClick={() => navigate(`/production?plan=${p.id}`)}
                  className="text-left rounded-lg border border-[#E5E7EB] bg-white hover:border-[#D4AF37] hover:shadow-sm transition p-3"
                  data-testid={`plan-saved-card-${p.id}`}
                >
                  <div className="text-sm font-semibold text-[#0A3B24] truncate">{p.name}</div>
                  <div className="text-[11px] text-[#4B5563] mt-0.5">
                    {p.production_date || "—"} · {p.production_for || "—"} · {p.items?.length || 0} items
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-1">
                    <div className="text-[10px]">
                      <div className="text-[#4B5563]">Cost</div>
                      <div className="font-mono-num font-semibold text-[#0A3B24]">{fmtINR(p.totals?.total_cost || 0)}</div>
                    </div>
                    <div className="text-[10px]">
                      <div className="text-[#4B5563]">Weight</div>
                      <div className="font-mono-num font-semibold text-[#0A3B24]">{(p.totals?.total_weight_kg || 0).toFixed(2)} kg</div>
                    </div>
                    <div className="text-[10px]">
                      <div className="text-[#4B5563]">Portions</div>
                      <div className="font-mono-num font-semibold text-[#0A3B24]">{p.totals?.total_portions || 0}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div className="kostra-card p-4">
      <div className="flex items-start justify-between">
        <span className="kostra-data-label text-[10px]">{label}</span>
        <div className="h-8 w-8 rounded-md flex items-center justify-center" style={{ background: `${accent}10`, color: accent }}>
          <Icon size={16} weight="duotone" />
        </div>
      </div>
      <div className="mt-2 font-mono-num text-xl font-bold text-[#111827]">{value}</div>
    </div>
  );
}

/**
 * Sections C–F merged: Ingredient Requirement + Purchase Requisition + Actual Spend + Variance
 * One table with inline editing per row.
 */
function IngredientControlSection({
  requirements, totals, overrides, onOverrideChange,
  onDownloadRequisition, onEmailVendor,
}) {
  const grandTotals = totals || {};
  const variance = grandTotals.variance || 0;
  const variancePct = grandTotals.variance_pct || 0;
  const purchaseLines = (requirements || []).filter((r) => (r.purchase_qty_kg || 0) > 0);

  return (
    <section className="space-y-4" data-testid="ingredient-control-section">
      {/* Header strip */}
      <div className="kostra-card p-5 md:p-6" data-testid="ingredient-explosion">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-[#0A3B24]/10 text-[#0A3B24] flex items-center justify-center">
              <Cube size={20} weight="duotone" />
            </div>
            <div>
              <h2 className="kostra-section-title text-xl">Ingredient Requirement &amp; Purchase Control</h2>
              <p className="text-xs text-[#4B5563] mt-0.5">
                Auto-exploded from your recipes × produced portions. Edit <strong>Available Stock</strong> and
                <strong> Actual Cost</strong> inline — purchase qty &amp; variance recompute live.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="gold-pill text-[11px] font-semibold px-2.5 py-1 rounded-full">
              {requirements.length} ingredient{requirements.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>

        {/* Section C/D/E/F merged table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#4B5563] border-b-2 border-[#E5E7EB]">
                <th className="py-2 pr-1.5">#</th>
                <th className="py-2 pr-1.5">Ingredient</th>
                <th className="py-2 pr-1.5">Category</th>
                <th className="py-2 pr-1.5 text-right">Required</th>
                <th className="py-2 pr-1.5 text-right">Rate</th>
                <th className="py-2 pr-1.5 text-right bg-[#FDF9EE]/40">Planned Cost</th>
                <th className="py-2 pr-1.5 text-right bg-[#FDF9EE]/40">Avail. Stock</th>
                <th className="py-2 pr-1.5 text-right bg-[#FDF9EE]/40">Purchase Qty</th>
                <th className="py-2 pr-1.5 text-right">Actual Cost</th>
                <th className="py-2 pl-1.5 text-right">Variance</th>
              </tr>
            </thead>
            <tbody>
              {requirements.map((ing, i) => {
                const ov = overrides[ing.key] || {};
                const stock = ov.available_stock_kg ?? ing.available_stock_kg ?? 0;
                const actual = ov.actual_cost ?? ing.actual_cost ?? 0;
                return (
                  <tr key={ing.key || i} className="border-b border-[#F1F2F4] hover:bg-[#FDF9EE]/30 transition" data-testid={`ing-row-${i}`}>
                    <td className="py-2 pr-1.5 font-mono-num text-[#4B5563]">{i + 1}</td>
                    <td className="py-2 pr-1.5 font-semibold text-[#0A3B24]">{ing.name}</td>
                    <td className="py-2 pr-1.5 text-[11px] text-[#4B5563]">{ing.category}</td>
                    <td className="py-2 pr-1.5 text-right font-mono-num">{ing.total_required_kg.toFixed(3)} kg</td>
                    <td className="py-2 pr-1.5 text-right font-mono-num text-[#4B5563]">{fmtINR(ing.rate_per_kg)}</td>
                    <td className="py-2 pr-1.5 text-right font-mono-num font-bold text-[#0A3B24] bg-[#FDF9EE]/30">{fmtINR(ing.planned_cost)}</td>
                    <td className="py-1 pr-1.5 bg-[#FDF9EE]/30">
                      <Input
                        type="number" min="0" step="0.1"
                        value={stock}
                        onChange={(e) => onOverrideChange(ing.key, { available_stock_kg: parseFloat(e.target.value) || 0 })}
                        className="h-8 text-right font-mono-num text-xs border-[#D4AF37]/40 focus-visible:border-[#0A3B24]"
                        data-testid={`ing-stock-${i}`}
                      />
                    </td>
                    <td className="py-2 pr-1.5 text-right font-mono-num font-semibold text-[#DC2626] bg-[#FDF9EE]/30" data-testid={`ing-purchase-${i}`}>
                      {ing.purchase_qty_kg > 0 ? `${ing.purchase_qty_kg.toFixed(3)} kg` : "—"}
                    </td>
                    <td className="py-1 pr-1.5">
                      <Input
                        type="number" min="0" step="1"
                        value={actual}
                        onChange={(e) => onOverrideChange(ing.key, { actual_cost: parseFloat(e.target.value) || 0 })}
                        placeholder="—"
                        className="h-8 text-right font-mono-num text-xs"
                        data-testid={`ing-actual-${i}`}
                      />
                    </td>
                    <td className="py-2 pl-1.5 text-right font-mono-num font-semibold" data-testid={`ing-variance-${i}`}>
                      {actual > 0 ? (
                        <span className={ing.variance > 0 ? "text-[#DC2626]" : (ing.variance < 0 ? "text-[#16A34A]" : "text-[#4B5563]")}>
                          {ing.variance > 0 ? "+" : ""}{fmtINR(ing.variance)}
                          <div className="text-[9px] font-normal">
                            ({ing.variance_pct > 0 ? "+" : ""}{ing.variance_pct.toFixed(1)}%)
                          </div>
                        </span>
                      ) : <span className="text-[#9CA3AF]">—</span>}
                    </td>
                  </tr>
                );
              })}
              <tr className="bg-[#0A3B24] text-white font-bold" data-testid="ing-totals-row">
                <td className="py-3 pr-1.5" colSpan={5}>TOTAL</td>
                <td className="py-3 pr-1.5 text-right font-mono-num">{fmtINR(grandTotals.planned_cost || 0)}</td>
                <td className="py-3 pr-1.5" />
                <td className="py-3 pr-1.5 text-right font-mono-num">{fmtINR(grandTotals.purchase_value || 0)}</td>
                <td className="py-3 pr-1.5 text-right font-mono-num">{fmtINR(grandTotals.actual_cost || 0)}</td>
                <td className="py-3 pl-1.5 text-right font-mono-num">
                  {grandTotals.actual_cost > 0 ? (
                    <span>{variance > 0 ? "+" : ""}{fmtINR(variance)}
                      <div className="text-[10px] font-normal">({variance > 0 ? "+" : ""}{variancePct.toFixed(2)}%)</div>
                    </span>
                  ) : "—"}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Action strip */}
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-[#E5E7EB]">
          <div className="flex flex-wrap gap-3 text-xs">
            <Stat label="Planned Cost" value={fmtINR(grandTotals.planned_cost || 0)} accent="#0A3B24" />
            <Stat label="Purchase Value" value={fmtINR(grandTotals.purchase_value || 0)} accent="#D4AF37" />
            <Stat label="Actual Spend" value={fmtINR(grandTotals.actual_cost || 0)} accent="#0E5232" />
            <Stat label="Variance" value={`${variance > 0 ? "+" : ""}${fmtINR(variance)} (${variancePct.toFixed(2)}%)`}
                  accent={variance > 0 ? "#DC2626" : (variance < 0 ? "#16A34A" : "#4B5563")} />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={onEmailVendor}
              disabled={purchaseLines.length === 0}
              variant="outline"
              className="bg-white border-[#0A3B24]/30 text-[#0A3B24] hover:bg-[#FDF9EE] font-semibold h-10 text-xs"
              data-testid="ing-email-vendor-btn"
            >
              <Receipt size={14} weight="bold" className="mr-1.5" /> Email Vendor
            </Button>
            <Button
              onClick={onDownloadRequisition}
              disabled={purchaseLines.length === 0}
              className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold h-10 text-xs"
              data-testid="ing-download-requisition-btn"
            >
              <ShoppingCart size={14} weight="bold" className="mr-1.5" /> Download Purchase Requisition
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value, accent = "#111827" }) {
  return (
    <div className="px-3 py-2 rounded-md border border-[#E5E7EB] bg-white">
      <div className="kostra-data-label text-[9px]">{label}</div>
      <div className="font-mono-num text-xs font-bold mt-0.5" style={{ color: accent }}>{value}</div>
    </div>
  );
}
